// ─────────────────────────────────────────────────────────────────
// Glass-i — Stage 6B.8A-3C: shared lookup-Select widgets used by
// every simple-create popup. Thin wrappers around AntD <Select>
// that:
//   • Fetch options lazily on first open / first keystroke (debounced).
//   • Cache the most-recent result so the dropdown opens instantly
//     on re-open without re-hitting the backend.
//   • Show the human label + a small code chip in the option row.
//   • Are controlled (value + onChange) so the parent AntD <Form>
//     binds them like a native <Select>.
//
// No business logic lives here — these are pure UI primitives so the
// per-kind modal files can stay short.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from 'react';
import { Input, Select } from 'antd';
import { studioLookups, itemsApi } from '../../itemsApi';
import { categoryApi, polishTypeApi, brandApi } from '../../../itemConfig/itemConfigApi';
import { uomApi } from '../../../uoms/uomApi';

const DEFAULT_FETCH_DEBOUNCE_MS = 200;

function makeLookupSelect(displayName, fetcher, mapRow, placeholderText, fetchOne) {
  function LookupSelect({
    value,
    onChange,
    disabled,
    placeholder,
    style,
    allowClear = true,
    extraParams,
    autoFocus
  }) {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const debounceRef = useRef();
    const loadedOnce  = useRef(false);

    const load = useCallback(async (q) => {
      setLoading(true);
      try {
        const params = { ...(extraParams || {}) };
        if (q && q.trim()) params.q = q.trim();
        const rows = await fetcher(params);
        setOptions(Array.isArray(rows) ? rows.map(mapRow) : []);
      } catch (_e) {
        // Friendly silent — caller still sees an empty dropdown but
        // never an unhandled crash. The modal's overall validation
        // catches required-but-unset.
      } finally {
        setLoading(false);
      }
    }, [extraParams]);

    // Preload on mount so edit-mode hydration can resolve the picked
    // value to a human label immediately (before the first dropdown
    // open). Most masters fit in a single page; the by-id graft below
    // covers the rare case where the picked row isn't in the first page.
    useEffect(() => {
      if (loadedOnce.current) return;
      loadedOnce.current = true;
      load('');
    }, [load]);

    // Edit mode — the parent hydrates the form with a UUID. If the
    // picked row isn't in the loaded options yet, fetch it by id and
    // graft it so AntD renders its label instead of the raw UUID.
    useEffect(() => {
      if (!value) return;
      if (typeof fetchOne !== 'function') return;
      if (options.some((o) => o.value === value)) return;
      let cancelled = false;
      fetchOne(value)
        .then((row) => {
          if (cancelled || !row) return;
          const mapped = mapRow(row);
          if (!mapped || !mapped.value) return;
          setOptions((prev) => (
            prev.some((o) => o.value === mapped.value) ? prev : [...prev, mapped]
          ));
        })
        .catch(() => { /* fall back to bare UUID until next load */ });
      return () => { cancelled = true; };
    }, [value, options]);

    const onOpenChange = useCallback((open) => {
      if (open && !loadedOnce.current) {
        loadedOnce.current = true;
        load('');
      }
    }, [load]);

    const onSearch = useCallback((text) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => load(text), DEFAULT_FETCH_DEBOUNCE_MS);
    }, [load]);

    return (
      <Select
        style={{ width: '100%', ...(style || {}) }}
        placeholder={placeholder || placeholderText}
        value={value || undefined}
        onChange={(v) => onChange && onChange(v)}
        onSearch={onSearch}
        onDropdownVisibleChange={onOpenChange}
        showSearch
        filterOption={false}
        allowClear={allowClear}
        loading={loading}
        disabled={disabled}
        autoFocus={autoFocus}
        notFoundContent={loading ? 'Loading…' : 'No matches'}
        options={options}
      />
    );
  }
  LookupSelect.displayName = displayName;
  return LookupSelect;
}

// ── Item category ────────────────────────────────────────────────
export const ItemCategorySelect = makeLookupSelect(
  'ItemCategorySelect',
  (params) => studioLookups.itemCategories(params),
  (row) => ({
    value: row.id,
    label: row.categoryName || row.category_name || row.label || row.id,
    code:  row.categoryCode || row.category_code || ''
  }),
  'Select category…',
  (id) => categoryApi.get(id)
);

// ── UOM ──────────────────────────────────────────────────────────
// Display short CODE (SQM / KGS / PCS / RFT) — operators recognise the
// code on the shop floor and it reads cleaner in a narrow column. The
// full name is shown as a secondary chip inside the dropdown row but
// is not the searchable label. Picker is strict — typing only filters,
// it never lets a user save free text.
export const UomSelect = makeLookupSelect(
  'UomSelect',
  (params) => studioLookups.uoms(params),
  (row) => ({
    value: row.id,
    label:
      row.uomCode || row.uom_code ||
      row.uomName || row.uom_name ||
      row.label   || row.id,
    code:
      row.uomName || row.uom_name || ''
  }),
  'Select UOM code…',
  (id) => uomApi.get(id)
);

// ── Brand (Stage 6B.8A-3D) ───────────────────────────────────────
// Display brand CODE first (e.g. "SAINT_GOBAIN") so the narrow column
// stays readable on the simple-create popups. The Brand Master tab
// holds the full Name / Display Name / Country / Website fields.
export const BrandSelect = makeLookupSelect(
  'BrandSelect',
  (params) => studioLookups.brands(params),
  (row) => ({
    value: row.id,
    label:
      row.brandCode  || row.brand_code  ||
      row.brandName  || row.brand_name  ||
      row.displayName || row.display_name ||
      row.label       || row.id,
    code:
      row.brandName  || row.brand_name  || ''
  }),
  'Select brand…',
  (id) => brandApi.get(id)
);

// ── Polish type (glass-only) ─────────────────────────────────────
// Display the short polish CODE (e.g. "CEP", "FLT") rather than the
// long polish name — operators type the code on the shop floor and
// the code is unique by definition, so it reads cleaner in a narrow
// dropdown column. Value still binds to row.id (UUID).
export const PolishTypeSelect = makeLookupSelect(
  'PolishTypeSelect',
  (params) => studioLookups.polishTypes(params),
  (row) => ({
    value: row.id,
    label:
      row.polishTypeCode  || row.polish_type_code ||
      row.polishCode      || row.polish_code      ||
      row.label           || row.id,
    code:
      row.polishTypeName  || row.polish_type_name || ''
  }),
  'Select polish code…',
  (id) => polishTypeApi.get(id)
);

// ── Stock-item picker (with free-text fallback) ──────────────────
//
// This one is special: the brief allows the user to either pick an
// existing stock item (→ sourceStockItemId) OR type a description
// that doesn't match anything (→ stockDesc free text). We expose
// both via two callbacks: onPick(item) sets sourceStockItemId, and
// onChangeText(text) sets stockDesc. The parent <Form> uses the
// `dependencies` prop to validate that at least one is present
// where required (Glass popup).
export function StockItemSelect({
  pickedId,
  textValue,
  onPick,
  onChangeText,
  disabled,
  productName,    // when set, auto-fills stockDesc in self mode
  excludeId,      // id of the item being edited — hidden from the picker so it can't parent itself
  mode = 'self',  // 'self' = mirrors product name; 'derived' = picker active. Controlled by parent.
  placeholder = 'Pick existing base stock or type a description'
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();
  // Tracks whether the user has typed in the search box. While false
  // we keep auto-filtering by productName; once they type we honour
  // their query instead.
  const userTypedRef = useRef(false);

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      const result = await itemsApi.lookup({
        q: q || '',
        isStockItem: true,
        rootStockOnly: true,   // only true root stocks — never a derived row
        excludeId: excludeId || undefined,
        status: 'active',
        limit: 50
      });
      // The lookup endpoint wraps its payload as `{ rows: [...] }` (see
      // erpItems.controller.js → `data: { rows }`). Accept both shapes
      // so a future API change cannot silently empty the dropdown again.
      const rows = Array.isArray(result)
        ? result
        : (result && Array.isArray(result.rows) ? result.rows : []);
      setOptions(rows.map((r) => ({
        value: r.id,
        label: r.itemName || r.item_name || r.label || r.id,
        code:  r.itemCode || r.item_code || ''
      })));
    } catch (_e) {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [excludeId]);

  // Self mode — mirror Product Name into stockDesc whenever either
  // changes. Skipped while the parent has set mode='derived' so a
  // stale typed search doesn't get overwritten.
  useEffect(() => {
    if (mode !== 'self') return;
    const target = productName || '';
    if (textValue === target) return;
    onChangeText && onChangeText(target);
  }, [mode, productName, textValue, onChangeText]);

  // Reset the userTyped tracker whenever the parent flips mode so a
  // re-entry into derived mode starts fresh.
  useEffect(() => { userTypedRef.current = false; }, [mode]);

  useEffect(() => {
    // Pre-load once on mount so the first open is instant.
    load('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Edit mode — the parent passes back a UUID from the previously
  // saved item. That UUID is unlikely to be in the lookup's paginated
  // first page, so AntD would otherwise show the raw UUID instead of
  // the item's name. Pull the picked item by id and graft it into the
  // options list so the Select can render its label.
  useEffect(() => {
    if (!pickedId) return;
    if (options.some((o) => o.value === pickedId)) return;
    let cancelled = false;
    itemsApi.get(pickedId)
      .then((row) => {
        if (cancelled || !row || !row.id) return;
        setOptions((prev) => (
          prev.some((o) => o.value === row.id)
            ? prev
            : [...prev, {
                value: row.id,
                label: row.itemName || row.item_name || row.id,
                code:  row.itemCode || row.item_code || ''
              }]
        ));
      })
      .catch(() => { /* picker can still fall back to textValue */ });
    return () => { cancelled = true; };
  }, [pickedId, options]);

  // Stage 6B.8A-3H follow-up — the productName auto-filter was
  // intentionally removed from the derived-mode picker. It used to
  // pre-seed the lookup with the current Product Name, which hid
  // perfectly valid root stocks whenever the new variant's name did
  // not happen to share a substring with an existing parent. In
  // derived mode we now always start from a full root-stock list and
  // let the user narrow it with their own search.

  // Drive both pickedId (UUID) and the free text. AntD <Select> can
  // be a "tags" mode that allows arbitrary input — but tags returns
  // an array. Easier: render two binds. When the user picks an
  // option → call onPick(id) + clear text. When they search but
  // don't pick → keep the typed string in onChangeText so the modal
  // can fall back to stockDesc on save.
  const onSearch = useCallback((text) => {
    userTypedRef.current = Boolean(text);
    onChangeText && onChangeText(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(text), DEFAULT_FETCH_DEBOUNCE_MS);
  }, [onChangeText, load]);

  const onDropdownVisibleChange = useCallback((open) => {
    if (!open) return;
    // Always show the full root-stock list on open unless the user
    // already has an active search. The picker is for picking an
    // existing parent, so a complete list is more useful than a
    // productName-filtered one.
    if (userTypedRef.current) return;
    load('');
  }, [load]);

  const onSelectChange = useCallback((value) => {
    if (!value) {
      onPick && onPick(null);
      return;
    }
    const picked = options.find((o) => o.value === value);
    onPick && onPick(value);
    if (picked && onChangeText) onChangeText(picked.label);
  }, [options, onPick, onChangeText]);

  // Display priority:
  //  1. If the picked UUID resolves to a loaded option, use the UUID so
  //     AntD renders the option's label.
  //  2. Else fall back to textValue (stockDesc) so the user sees what
  //     they typed — or the snapshot label from a previous edit.
  //  3. Else fall back to the bare UUID (very brief — only between the
  //     hydration and the by-id resolve effect above).
  const displayValue = pickedId && options.some((o) => o.value === pickedId)
    ? pickedId
    : (textValue || pickedId || undefined);

  if (mode === 'self') {
    return (
      <Input
        value={productName || ''}
        disabled
        placeholder="Stock name will mirror the Product Name"
      />
    );
  }
  return (
    <Select
      style={{ width: '100%' }}
      placeholder={placeholder}
      value={displayValue}
      onChange={onSelectChange}
      onSearch={onSearch}
      onDropdownVisibleChange={onDropdownVisibleChange}
      showSearch
      filterOption={false}
      allowClear
      loading={loading}
      disabled={disabled}
      notFoundContent={loading ? 'Loading…' : 'Type to search or just enter a description'}
      options={options.map((o) => ({
        value: o.value,
        label: (
          <span>
            {o.code ? (
              <code style={{ marginRight: 6, fontSize: 11, color: '#888' }}>{o.code}</code>
            ) : null}
            {o.label}
          </span>
        )
      }))}
    />
  );
}
StockItemSelect.displayName = 'StockItemSelect';

// ── GST rate (Stage 6B.8A-3F) ────────────────────────────────────
// Strict picker: operator cannot type a free-text percent. Bound to
// the Form.Item field `gstRateId` (UUID). On pick the component also
// writes the rate snapshot to `gstRatePercent` via the parent form
// so existing payload contracts (which carry both id + percent)
// continue to work without controller changes.
//
// Why dual-write instead of derive-on-server: keeps the snapshot
// honest — if an operator updates the tax_rates master tomorrow with a
// new percent, the item still records the percent that was effective
// when it was last edited.
export function GstRateSelect({
  value,
  onChange,
  form,
  disabled,
  placeholder = 'Select GST rate…',
  allowClear = true,
  autoFocus
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const loadedOnce = useRef(false);
  const debounceRef = useRef();

  const realLoad = useCallback(async (q) => {
    setLoading(true);
    try {
      const rows = await studioLookups.taxRates({ q: q || '', limit: 100 });
      setOptions((Array.isArray(rows) ? rows : []).map((r) => {
        const pct = r.gstRate != null ? Number(r.gstRate) : 0;
        return {
          value: r.id,
          label: `${r.taxCode || ''} · ${pct.toFixed(2)}%`,
          taxCode: r.taxCode || '',
          taxName: r.taxName || '',
          gstRate: pct,
          taxType: r.taxType || 'gst'
        };
      }));
    } catch (_e) {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Preload on mount so edit-mode hydration sees the picked option
  // before the user opens the dropdown. GST master is small (100 lim)
  // so a single load typically covers every rate the picker can hit.
  useEffect(() => {
    if (loadedOnce.current) return;
    loadedOnce.current = true;
    realLoad('');
  }, [realLoad]);

  const onOpenChange = useCallback((open) => {
    if (open && !loadedOnce.current) {
      loadedOnce.current = true;
      realLoad('');
    }
  }, [realLoad]);

  const onSearch = useCallback((text) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => realLoad(text), DEFAULT_FETCH_DEBOUNCE_MS);
  }, [realLoad]);

  const handleChange = useCallback((newId) => {
    if (onChange) onChange(newId || null);
    if (!form) return;
    if (!newId) {
      form.setFieldValue('gstRatePercent', 0);
      return;
    }
    const picked = options.find((o) => o.value === newId);
    form.setFieldValue('gstRatePercent',
      picked && Number.isFinite(picked.gstRate) ? picked.gstRate : 0);
  }, [onChange, form, options]);

  return (
    <Select
      style={{ width: '100%' }}
      placeholder={placeholder}
      value={value || undefined}
      onChange={handleChange}
      onSearch={onSearch}
      onDropdownVisibleChange={onOpenChange}
      showSearch
      filterOption={false}
      allowClear={allowClear}
      loading={loading}
      disabled={disabled}
      autoFocus={autoFocus}
      notFoundContent={loading ? 'Loading…' : 'No GST rates'}
      options={options}
      optionRender={(opt) => (
        <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ fontWeight: 500 }}>
            {opt.data.taxCode}
            <span style={{ marginLeft: 8, color: '#64748b', fontWeight: 400 }}>
              {opt.data.taxName}
            </span>
          </span>
          <span style={{
            color: '#0f172a', fontVariantNumeric: 'tabular-nums', fontWeight: 600
          }}>{Number(opt.data.gstRate).toFixed(2)}%</span>
        </span>
      )}
    />
  );
}
GstRateSelect.displayName = 'GstRateSelect';

export default {
  ItemCategorySelect,
  UomSelect,
  PolishTypeSelect,
  BrandSelect,
  GstRateSelect,
  StockItemSelect
};
